import React, { useState, useEffect, useCallback } from 'react';
import { Smartphone } from 'lucide-react';
import './InstallPWA.css';

// Mémoire persistante du choix utilisateur (localStorage, pas sessionStorage :
// le refus doit survivre à la fermeture du navigateur).
const CHOICE_KEY = 'pwaInstallChoice';
const SNOOZE_DAYS = 90;

function readChoice() {
  try {
    return JSON.parse(localStorage.getItem(CHOICE_KEY) || 'null');
  } catch (e) {
    return null;
  }
}

function saveChoice(status) {
  try {
    localStorage.setItem(CHOICE_KEY, JSON.stringify({ status, ts: Date.now() }));
  } catch (e) {
    /* mode privé / quota : on ignore */
  }
}

// Doit-on encore proposer l'installation ?
function shouldOffer() {
  const choice = readChoice();
  if (!choice) return true;
  // Installée ou acceptée : on ne repropose plus jamais.
  if (choice.status === 'installed' || choice.status === 'accepted') return false;
  // Refusée : on attend SNOOZE_DAYS avant de reproposer.
  return Date.now() - (choice.ts || 0) > SNOOZE_DAYS * 24 * 60 * 60 * 1000;
}

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true
  );
}

function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  const dismiss = useCallback((status) => {
    saveChoice(status);
    setShowInstallBanner(false);
    setShowIOSInstructions(false);
  }, []);

  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const iOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    setIsIOS(iOS);

    // Déjà installée : on mémorise définitivement et on n'affiche rien.
    if (isStandalone()) {
      saveChoice('installed');
      return undefined;
    }

    // L'app a été installée pendant la session (ou depuis le menu du navigateur).
    const handleAppInstalled = () => {
      saveChoice('installed');
      setShowInstallBanner(false);
      setShowIOSInstructions(false);
      window.deferredPrompt = null;
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    // L'utilisateur a déjà répondu (oui ou non) : on ne le sollicite plus.
    if (!isMobile || !shouldOffer()) {
      return () => window.removeEventListener('appinstalled', handleAppInstalled);
    }

    // iOS : pas de beforeinstallprompt, on propose la bannière d'instructions.
    if (iOS) {
      setShowInstallBanner(true);
    } else if (window.deferredPrompt) {
      // Android : bannière discrète. AUCUN appel automatique à prompt() :
      // le prompt natif ne s'ouvre que sur clic explicite du bouton « Installer ».
      setDeferredPrompt(window.deferredPrompt);
      setShowInstallBanner(true);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      setDeferredPrompt(e);
      if (shouldOffer()) setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    const prompt = deferredPrompt || window.deferredPrompt;
    if (prompt) {
      try {
        await prompt.prompt();
        const { outcome } = await prompt.userChoice;
        // Dans les deux cas le choix est mémorisé : accepté = jamais reproposé,
        // refusé = pas avant SNOOZE_DAYS.
        dismiss(outcome === 'accepted' ? 'accepted' : 'dismissed');
      } catch (error) {
        console.error('Erreur installation:', error);
        dismiss('dismissed');
      }
      setDeferredPrompt(null);
      window.deferredPrompt = null;
    } else {
      alert('Pour installer l\'application :\n\n' +
            'Chrome Android: Menu (⋮) → "Ajouter à l\'écran d\'accueil"\n' +
            'Firefox Android: Menu → "Installer"\n' +
            'Samsung Internet: Menu → "Ajouter page à"');
      dismiss('dismissed');
    }
  };

  const handleClose = () => dismiss('dismissed');

  const handleCloseIOSInstructions = () => dismiss('dismissed');

  if (!showInstallBanner) {
    return null;
  }

  return (
    <>
      <div className="install-pwa-banner">
        <div className="install-pwa-content">
          <div className="install-pwa-icon">
            <img src="/icon-192.png" alt="E-Shops Connect" />
          </div>
          <div className="install-pwa-text">
            <h3>Installer E-Shops Connect</h3>
            <p>Ajoutez l'application à votre écran d'accueil pour un accès rapide</p>
          </div>
          <div className="install-pwa-actions">
            <button onClick={handleInstallClick} className="btn-install">
              <Smartphone size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Installer
            </button>
            <button onClick={handleClose} className="btn-close-banner">
              ✕
            </button>
          </div>
        </div>
      </div>

      {/* Modal d'instructions pour iOS */}
      {showIOSInstructions && (
        <div className="ios-instructions-overlay">
          <div className="ios-instructions-modal">
            <button className="ios-close-btn" onClick={handleCloseIOSInstructions}>✕</button>
            <div className="ios-instructions-content">
              <img src="/icon-192.png" alt="E-Shops Connect" className="ios-app-icon" />
              <h2>Installer E-Shops Connect</h2>
              <p className="ios-subtitle">Pour installer l'application sur votre iPhone/iPad :</p>

              <div className="ios-steps">
                <div className="ios-step">
                  <div className="ios-step-number">1</div>
                  <div className="ios-step-text">
                    <strong>Appuyez sur</strong> le bouton <span className="ios-share-icon">
                      <svg width="20" height="24" viewBox="0 0 20 24" fill="none">
                        <path d="M10 0L10 14M10 0L6 4M10 0L14 4" stroke="#007AFF" strokeWidth="2"/>
                        <rect x="0" y="16" width="20" height="8" rx="2" stroke="#007AFF" strokeWidth="2"/>
                      </svg>
                    </span> <strong>Partager</strong> en bas de l'écran
                  </div>
                </div>

                <div className="ios-step">
                  <div className="ios-step-number">2</div>
                  <div className="ios-step-text">
                    <strong>Faites défiler</strong> et sélectionnez <strong>"Sur l'écran d'accueil"</strong>
                  </div>
                </div>

                <div className="ios-step">
                  <div className="ios-step-number">3</div>
                  <div className="ios-step-text">
                    <strong>Appuyez sur</strong> <strong>"Ajouter"</strong> en haut à droite
                  </div>
                </div>
              </div>

              <div className="ios-benefits">
                <p>✓ Accès rapide depuis l'écran d'accueil</p>
                <p>✓ Mode plein écran sans barre d'adresse</p>
                <p>✓ Fonctionne hors ligne</p>
              </div>

              <button onClick={handleCloseIOSInstructions} className="ios-got-it-btn">
                J'ai compris
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default InstallPWA;
